import ToolsCanvas from "@/components/common/annotation/tools/tools-canvas.vue";
import TextControl from "@/components/common/annotation/text-control/text-control.vue";
import { useFabricObject } from "@/hooks/use-fabric-object";
import { useCursorRenderer } from "@/hooks/use-cursor-renderer";
import { useWhiteboardMediaState } from "@/hooks/use-whiteboard-media-state";
import { TeacherClass, TeacherClassLocale, ToolboxCanvas } from "@/locales/localeid";
import { MediaType, MousePositionEventData } from "@/models";
import { XYCoords } from "@/store/annotation-v2/state";
import { Exposure, ExposureItemMedia, ExposureItemMediaImage } from "@/store/lesson/state";
import { Colors, MAX_ZOOM_RATIO, MIN_ZOOM_RATIO, ZOOM_STEP } from "@/utils/constant";
import {
  ALTERNATE_AUDIO_ID,
  ALTERNATE_VIDEO_ID,
  createGuid,
  Cursor,
  DEFAULT_ALTERNATE_CONTENT_VOLUME,
  DEFAULT_ALTERNATE_CONTENT_VOLUME_FOR_HELPER,
  DefaultCanvasDimension,
  generateRealtimeLineId,
  isRotatedLPSlide,
  AnnotationMode,
  Tools,
} from "@/utils/utils";
import { AnnotationMode as AnnotationModeModel } from "@/models";
import { CloseOutlined, DownOutlined, UpOutlined } from "@ant-design/icons-vue";
import { EyeIcon, EyeSlashIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon, ViewfinderCircleIcon } from "@heroicons/vue/24/outline";
import { InformationCircleIcon } from "@heroicons/vue/24/solid";
import { useScroll } from "@vueuse/core";
import { Button, Modal, Popover, Radio, Space, Switch, Tooltip } from "ant-design-vue";
import { fabric } from "fabric";
import { computed, defineComponent, nextTick, onMounted, onUnmounted, PropType, reactive, Ref, ref, watch } from "vue";
import { fmtMsg } from "vue-glcommonui";
import VuePdfEmbed from "vue-pdf-embed";
import { useStore } from "vuex";
import { useFocusWord } from "@/hooks/use-focus-word";
import { useWhiteboard } from "@/hooks/use-whiteboard";
import { initCanvas, initConf } from "@/hooks/use-canvas";
import { useCanvasObjectUpdate } from "@/hooks/use-canvas-object-update";
import { useCanvasFullSync } from "@/hooks/use-canvas-full-sync";
import {
  FbObjects,
  FbEvents,
  findLatestCreatedItem,
  getAllObjectsExceptBackgroundAndTargets,
  ConnectObjectTypes,
  FbObjectOrigins,
  getAllUndoableObjects,
  isLessonBackgroundObject,
  isTargetObject,
  clearCanvasExceptBackgroundAndTargets,
  setTargetsVisibility,
  applyWhiteboardBackground,
  applyLessonBackground,
  setGroupVisibility,
  setCanvasDrawingMode,
  CANVAS_ID,
  clearAllCanvasObjects,
  DEFAULT_LESSON_IMAGE_URL,
} from "@/utils/fabric-utils";
import { TeacherState } from "@/store/room/interface";
import { useKeystrokeOnCanvas } from "@/hooks/use-keystroke-on-canvas";
import { DEFAULT_FONT_SIZE, Fonts } from "@/components/common/annotation/text-control/text-control";
import { useImageLoader } from "@/hooks/use-image-loader";
import { CustomPropertiesForTarget, useTarget } from "@/hooks/use-target";
import { useActiveElement } from "@vueuse/core";
import { debounce } from "lodash";
import { vuexName, MODULES } from "@/store/utils";
import { useAnnotationStore } from "@/hooks/use-annotation-store";
import { REALTIME_CHANNEL, resolveChannelName } from "@/ws";
import { TeacherRoomManager } from "@/manager/room/teacher.manager";
import { useRealtimeSpace } from "@/hooks/use-realtime-space";
import { useRealtimeDrawing } from "@/hooks/use-realtime-drawing";
import { useRemoteRequests } from "@/hooks/use-remote-requests.";

/* ----------------------------------------------------------------
 * Types & constants
 * --------------------------------------------------------------*/

export interface ControlTextState {
  fontSize?: number;
  fill?: string;
  fontFamily?: string;
}
export const ToolsBlockClickOnTarget = [Tools.TextBox, Tools.Laser, Tools.Pen];

const DIFF_BETWEEN_POINT = 6;
const DEFAULT_ZOOM_PERCENT = 100;
const COMPONENT_NAME = "WhiteboardPalette";

export default defineComponent({
  name: COMPONENT_NAME,
  props: {
    image: Object as PropType<ExposureItemMediaImage>,
    id: String,
    name: String,
  },
  components: {
    ToolsCanvas,
    TextControl,
    Space,
    Button,
    VuePdfEmbed,
    RadioButton: Radio.Button,
    RadioGroup: Radio.Group,
    DownOutlined,
    UpOutlined,
    CloseOutlined,
    Modal,
    Popover,
    MagnifyingGlassMinusIcon,
    MagnifyingGlassPlusIcon,
    Switch,
    EyeIcon,
    EyeSlashIcon,
    ViewfinderCircleIcon,
    Tooltip,
    InformationCircleIcon,
  },
  setup(props) {
    /* ----------------------------------------------------------------
     * Core canvas / board state
     * --------------------------------------------------------------*/

    let canvas: any;
    // group variable is used to store the group (fabric.js) containing the image and the annotations (targets)
    let group: any;
    let point: any;

    const toolNames: string[] = Object.values(Tools);
    const toolSelected: Ref<Tools> = ref(Tools.Laser);
    const strokeColor: Ref<string> = ref(Colors.Black);

    /* ----------------------------------------------------------------
     * Store, hooks & shared state
     * --------------------------------------------------------------*/

    const { dispatch, commit, getters } = useStore();

    const { requestZoomSlide, requestMoveZoomedSlide } = useRemoteRequests();

    const { videoRef, audioRef, debounceSendApiUpdateMediaState, toggleMediaCanPlay } = useWhiteboardMediaState();
    const { isWbVisible } = useWhiteboard();
    const { resetReloadKey, reloadKey, handleImageLoadError } = useImageLoader();
    const { focusWordContent } = useFocusWord();

    const { naturalWidthOfCurrentSlide, naturalHeightOfCurrentSlide } = useAnnotationStore();

    const whiteBoardRef = ref<HTMLDivElement>();

    // Use composable for realtime drawing (Helper only)
    const { handleDrawingData, clearAllPaths, removePathsByObjectId } = useRealtimeDrawing({
      defaultStrokeColor: Colors.Black,
    });

    const {
      handlePressRemoveIcon,
      handleEditObjectFromCanvas,
      handleRemoveObjectFromCanvas,
      handleAddObjectFromCanvas,
      handleRemoveAllObjectsFromCanvas,
    } = useCanvasObjectUpdate({
      onClearAll: () => clearAllPaths(),
      onRemoveObject: (objectId) => removePathsByObjectId(objectId),
    });

    /* ----------------------------------------------------------------
     * Fabric object helpers (text, shapes, sync)
     * --------------------------------------------------------------*/

    const {
      createTextBox,
      onTextBoxEdited,
      onObjectModified,
      isEditing,
      onObjectCreated,
      onLineCompleted,
      nextColor,
      updateSelectedObjectStyles,
      FontLoader,
      onObjectRemoved,
      onObjectSelected,
      onSelectionChanged,
      updateNextStyles,
      isGroup,
      lineObjectId,
      createCircle,
      createSquare,
      createLine,
    } = useFabricObject();

    useCanvasFullSync();
    useKeystrokeOnCanvas();

    const { initializeTargets, handleClickToggleAllTargetsBtn, isAllTargetsOnCanvasVisible, isAllTargetsOnCanvasTransparent } = useTarget({
      isBlockClickOnTarget: computed(() => ToolsBlockClickOnTarget.includes(toolSelected.value)),
    });

    /* ----------------------------------------------------------------
     * Lesson / exposure state
     * --------------------------------------------------------------*/

    const currentExposureItemMedia = computed<ExposureItemMedia | undefined>(
      () => getters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE_ITEM_MEDIA)],
    );
    const currentExposure = computed<Exposure | undefined>(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE)]);

    const imgCoords = computed<XYCoords | undefined>(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_IMG_COORDS)]);

    const isEmptyExposure = computed(() => currentExposure.value && !currentExposureItemMedia.value);
    const isWhiteboardShrink = computed(() => (!currentExposure.value && !isWbVisible.value) || (isEmptyExposure.value && !isWbVisible.value));

    /* ----------------------------------------------------------------
     * Pointer / cursor state & live cursor space
     * --------------------------------------------------------------*/

    const pointerCoords = ref<XYCoords | null>(null);
    const isPointerMode = computed(() => getters[vuexName(MODULES.ANNOTATION_V2.GETTERS.IS_POINTER_MODE)]);

    // re-bind pointerStyle to our pointerCoords
    const { pointerStyle } = useCursorRenderer(whiteBoardRef, pointerCoords);

    const isTeacherUseOnly = computed(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_IS_TEACHER_USE_ONLY)]);
    const teacher = computed<TeacherState | undefined>(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_TEACHER)]);
    const oneToOneStudentId = computed(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_ONE_TO_ONE_STUDENT_ID)]);
    const isHelper = computed<boolean>(() => getters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)]);
    const canSendRealtimeAction = computed<boolean>(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_CAN_SEND_REALTIME_ACTION)]);
    const volume = computed(() => (isHelper.value ? DEFAULT_ALTERNATE_CONTENT_VOLUME_FOR_HELPER : DEFAULT_ALTERNATE_CONTENT_VOLUME));
    const roomManager = computed<TeacherRoomManager | undefined>(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_ROOM_MANAGER)]);
    const realtimeChannels = computed(() => getters[vuexName(MODULES.REALTIME_SETTINGS.GETTERS.GET_SETTINGS)]);

    const getRealtimeClient = () => roomManager.value?.WSClient?.getRealtimeTransport()?.getRealtimeClient();

    const resolveCursorChannelName = () => {
      const channelKey = oneToOneStudentId.value
        ? isHelper.value
          ? REALTIME_CHANNEL.HELPER_ONE_ONE
          : REALTIME_CHANNEL.TEACHER_ONE_ONE
        : REALTIME_CHANNEL.MAIN;
      return resolveChannelName(channelKey, realtimeChannels.value);
    };

    const {
      sendCursor,
      sendLaser,
      sendPencil,
      disconnect: disconnectLiveCursorSpace,
    } = useRealtimeSpace({
      getRealtime: getRealtimeClient,
      resolveChannel: resolveCursorChannelName,
      onCursor: (payload: MousePositionEventData) => {
        if (!payload || !isHelper.value) return;
        if (!isPointerMode.value) {
          commit(vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_ANNOTATION_MODE), AnnotationModeModel.PointerMode);
        }
        pointerCoords.value = { x: payload.x, y: payload.y };
      },
      onDrawing: (payload) => {
        // Only Helper receives drawing data from Teacher
        if (!payload?.drawingData || !isHelper.value || !canvas) return;
        handleDrawingData(payload);
      },
      enabled: () => !isHelper.value,
    });

    /* ----------------------------------------------------------------
     * Drawing / zoom / interaction state
     * --------------------------------------------------------------*/

    const lineId: Ref<string> = ref(generateRealtimeLineId());
    const strokeWidth: Ref<number> = ref(2);
    const selectedAnnotationMode: Ref<number> = ref(-1);

    const firstLoadImage: Ref<boolean> = ref(false);

    const video = ref<HTMLVideoElement | null>(null);
    const audio = ref<HTMLVideoElement | null>(null);

    const isDrawing: Ref<boolean> = ref(false);
    const prevPoint: Ref<XYCoords | undefined> = ref(undefined);
    const isMouseOver: Ref<boolean> = ref(false);
    const isMouseOut: Ref<boolean> = ref(false);

    const isShowPreviewCanvas = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_IS_SHOW_PREVIEW_CANVAS)]);

    const globalZoomRatio = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_ZOOM_RATIO)]);
    const localZoomRatio = ref(MIN_ZOOM_RATIO);
    const prevZoomRatio = ref(MIN_ZOOM_RATIO);
    const prevCoords = ref({ x: 0, y: 0 });
    const prevPdfScrollProgress = ref(0);
    const zoomPercentage = ref(DEFAULT_ZOOM_PERCENT);

    const prevLineId = ref("");
    const moveEventCounter = ref(0);
    const isPointerOutsideCanvas = ref(true);
    const pointsSkipped = ref<Array<XYCoords>>([]);

    const lineInProgress = ref<typeof fabric.Line>();

    const isTextBoxSelected = computed<boolean>(() => toolSelected.value === Tools.TextBox);

    /* ----------------------------------------------------------------
     * Text control state
     * --------------------------------------------------------------*/

    const controlTextState = reactive({
      fontSize: DEFAULT_FONT_SIZE,
      fill: Colors.Black,
      fontFamily: Fonts.DidactGothic,
    });

    // This variable is used to prevent the watcher from being triggered when "controlTextState" is updated
    let isControlTextStateWatcherActive = true;

    const updateControlTextState = (newProps: Partial<ControlTextState>, isPreventWatcher = false) => {
      isControlTextStateWatcherActive = true;
      Object.assign(controlTextState, newProps);
      if (isPreventWatcher) {
        isControlTextStateWatcherActive = false;
      }
    };

    /* ----------------------------------------------------------------
     * Board & canvas helpers
     * --------------------------------------------------------------*/
    const deactiveObject = () => {
      canvas?.discardActiveObject();
      canvas?.renderAll();
    };

    const clearCanvasExceptBackground = () => {
      canvas.remove(...canvas.getObjects().filter((item: any) => !isLessonBackgroundObject(item)));
    };

    const pauseMedia = () => {
      videoRef.value?.pause();
      audioRef.value?.pause();
    };

    /* ----------------------------------------------------------------
     * Zoom & panning helpers
     * --------------------------------------------------------------*/

    const zoomIn = async () => {
      if (localZoomRatio.value > MAX_ZOOM_RATIO) {
        return;
      }
      if (canvas.getZoom() !== localZoomRatio.value) {
        localZoomRatio.value = canvas.getZoom();
      }
      localZoomRatio.value = localZoomRatio.value + ZOOM_STEP > MAX_ZOOM_RATIO ? MAX_ZOOM_RATIO : localZoomRatio.value + ZOOM_STEP;
      canvas.zoomToPoint(point, localZoomRatio.value);
      canvas.forEachObject((o: any) => {
        o.setCoords();
      });
      canvas.calcOffset();
      zoomPercentage.value = Math.round(localZoomRatio.value * DEFAULT_ZOOM_PERCENT);

      const viewportTransform = canvas.viewportTransform;
      await requestZoomSlide({
        ratio: localZoomRatio.value,
        position: {
          x: Math.floor(viewportTransform[4]),
          y: Math.floor(viewportTransform[5]),
        },
      });
      commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), localZoomRatio.value, { root: true });
    };

    const zoomOut = async () => {
      if (canvas.getZoom() > MIN_ZOOM_RATIO) {
        if (canvas.getZoom() !== localZoomRatio.value) {
          localZoomRatio.value = canvas.getZoom();
        }
        localZoomRatio.value -= 0.1;
        if (localZoomRatio.value < MIN_ZOOM_RATIO) {
          localZoomRatio.value = MIN_ZOOM_RATIO;
        }
        zoomPercentage.value = Math.round(localZoomRatio.value * DEFAULT_ZOOM_PERCENT);
        canvas.zoomToPoint(point, localZoomRatio.value);
        const viewportTransform = canvas.viewportTransform;
        await requestZoomSlide({
          ratio: localZoomRatio.value,
          position: {
            x: Math.floor(viewportTransform[4]),
            y: Math.floor(viewportTransform[5]),
          },
        });
        commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), localZoomRatio.value, { root: true });
      }
    };

    const resetCanvasPanning = async () => {
      const viewport = canvas.viewportTransform;
      viewport[4] = 0;
      viewport[5] = 0;
      canvas.setViewportTransform(viewport);
      await requestMoveZoomedSlide({ x: 0, y: 0 });

      commit(
        vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS),
        { x: 0, y: 0 },
        {
          root: true,
        },
      );
    };

    const sendZoomingState = debounce(async (viewportTransform: any, localZoomRatioVal: number) => {
      await requestZoomSlide({
        ratio: localZoomRatioVal,
        position: {
          x: Math.floor(viewportTransform[4]),
          y: Math.floor(viewportTransform[5]),
        },
      });
    }, 300);

    /* ----------------------------------------------------------------
     * Preview canvas (snapshot) helpers
     * --------------------------------------------------------------*/

    const addSnapshotCanvasWithTargetsToPreview = () => {
      if (!group) {
        return;
      }
      group.clone(
        (cloned: any) => {
          cloned.visible = true;
          cloned.getObjects().forEach((obj: any) => {
            if (!isLessonBackgroundObject(obj.id)) {
              obj.fill = obj[CustomPropertiesForTarget.RealFill] ?? "transparent";
              obj.opacity = obj[CustomPropertiesForTarget.RealOpacity] ?? 1;
              obj.stroke = obj[CustomPropertiesForTarget.RealStroke] ?? "black";
            }
          });

          const objectToString = `{"objects":[${JSON.stringify(cloned)}], "background":"transparent"}`;
          commit(vuexName(MODULES.LESSON.COMMITS.SET_LESSON_PREVIEW_OBJECTS), objectToString, {
            root: true,
          });
        },
        ["id", CustomPropertiesForTarget.RealFill, CustomPropertiesForTarget.RealOpacity, CustomPropertiesForTarget.RealStroke],
      );
    };

    /* ----------------------------------------------------------------
     * Media type / URL & whiteboard toggle
     * --------------------------------------------------------------*/

    const mediaTypeId = computed(() => {
      const newId = props.id;
      let result = undefined;
      const listMedia = currentExposure.value?.alternateMediaBlockItems.flat();
      if (listMedia) {
        const target = listMedia.find((item) => newId === item.id);
        if (target) {
          result = target.mediaTypeId;
        }
      }
      return result;
    });

    const isValidUrl = computed(() => {
      return currentExposureItemMedia.value?.image.url !== DEFAULT_LESSON_IMAGE_URL;
    });

    const showHidePreviewModal = (isShowPreview = true) => {
      commit(vuexName(MODULES.LESSON.COMMITS.SET_SHOW_PREVIEW_CANVAS), isShowPreview, { root: true });
    };

    /* ----------------------------------------------------------------
     * Zoom ratio watcher & preview toggle watcher
     * --------------------------------------------------------------*/

    watch(globalZoomRatio, async (value) => {
      if (value == MIN_ZOOM_RATIO) {
        localZoomRatio.value = MIN_ZOOM_RATIO;
        zoomPercentage.value = DEFAULT_ZOOM_PERCENT;
        canvas.zoomToPoint(point, MIN_ZOOM_RATIO);
        if (group) {
          await resetCanvasPanning();
        }
      }
      if (globalZoomRatio.value && canvas.getZoom() !== globalZoomRatio.value) {
        localZoomRatio.value = globalZoomRatio.value;
        if (canvas && point) {
          canvas.zoomToPoint(point, globalZoomRatio.value);
        }
      }
      zoomPercentage.value = Math.floor(value.toFixed(2) * DEFAULT_ZOOM_PERCENT);
    });

    const disablePreviewBtn: Ref<boolean> = ref(false);
    watch(isShowPreviewCanvas, (currentValue) => {
      disablePreviewBtn.value = currentValue;
    });

    /* ----------------------------------------------------------------
     * Crop image / exposure & annotations meta
     * --------------------------------------------------------------*/

    const isCropImage = computed<boolean>(() => !!props.image?.metaData && props.image.metaData.width > 0 && props.image.metaData.height > 0);

    const targetsNum = computed(() => {
      const uniqueAnnotations: any[] = [];
      props.image?.metaData?.annotations?.forEach((metaDataObj: any) => {
        if (
          !uniqueAnnotations.some(
            (_obj: any) =>
              metaDataObj.color === _obj.color &&
              metaDataObj.height === _obj.height &&
              metaDataObj.width === _obj.width &&
              metaDataObj.opacity === _obj.opacity &&
              metaDataObj.rotate === _obj.rotate &&
              metaDataObj.type === _obj.type &&
              metaDataObj.x === _obj.x &&
              metaDataObj.y === _obj.y,
          )
        ) {
          uniqueAnnotations.push(metaDataObj);
        }
      });
      return uniqueAnnotations.length;
    });

    const hasTargets = computed(() => {
      return !!props.image?.metaData?.annotations && targetsNum.value;
    });

    const targetTextLocalize = computed(() => fmtMsg(TeacherClass.TargetText));
    const targetsTextLocalize = computed(() => fmtMsg(TeacherClass.TargetsText));

    const targetText = computed(() => {
      if (props.image?.metaData?.annotations?.length == 1) {
        return targetTextLocalize.value;
      } else {
        return targetsTextLocalize.value;
      }
    });

    /* ----------------------------------------------------------------
     * Mode switching (cursor / draw) & mouse-over-out state
     * --------------------------------------------------------------*/

    const setAnnotationMode = async (mode: AnnotationMode) => {
      selectedAnnotationMode.value = mode;
      await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_ANNOTATION_MODE), { mode });
    };
    const setCursorMode = () => {
      if (selectedAnnotationMode.value === AnnotationMode.Cursor) return;
      setAnnotationMode(AnnotationMode.Cursor);
    };
    const setDrawMode = () => {
      if (selectedAnnotationMode.value === AnnotationMode.Draw) return;
      setAnnotationMode(AnnotationMode.Draw);
    };

    const handleMouseOver = () => {
      isPointerOutsideCanvas.value = false;
    };
    const handleMouseOut = async () => {
      isPointerOutsideCanvas.value = true;
      // Send special cursor position to signal cursor should be hidden on student side
      if (toolSelected.value === Tools.Cursor && !isHelper.value) {
        await sendCursor({ x: -1, y: -1 });
      }
    };

    /* ----------------------------------------------------------------
     * Whiteboard mode (on/off) & canvas state switch
     * --------------------------------------------------------------*/

    const updateCanvasForWhiteboardMode = async (resetToolToPen = true) => {
      if (!canvas) return;
      clearCanvasExceptBackgroundAndTargets(canvas);
      clearAllPaths();
      if (isWbVisible.value) {
        setTargetsVisibility(canvas, false);
        applyWhiteboardBackground(canvas);
        setGroupVisibility(group, false);
        if (resetToolToPen) {
          await handleToolClick(Tools.Pen);
        }
      } else {
        setTargetsVisibility(canvas, true);
        applyLessonBackground(canvas);
        setGroupVisibility(group, true);
      }
      canvas.renderAll();
    };

    watch(isWbVisible, async () => {
      await updateCanvasForWhiteboardMode(false);
    });

    watch(
      () => currentExposureItemMedia.value?.teacherUseOnly && !isWbVisible.value,
      (val) => {
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_IS_TEACHER_USE_ONLY), !!val);
      },
    );

    /* ----------------------------------------------------------------
     * Realtime laser / pen helpers & cursor sending
     * --------------------------------------------------------------*/

    const handlePointsSkipped = (_: any) => {
      if (isDrawing.value) {
        const pointer = canvas.getPointer();
        if (!pointer) {
          return;
        }
        pointsSkipped.value.push({
          x: Math.floor(pointer.x),
          y: Math.floor(pointer.y),
        });
      }
    };

    const handleSendRealtimeLaserPoint = async (data: any) => {
      if (!data) return;
      const { data: laserData, isDone } = data;
      await sendLaser({
        x: laserData.points.x,
        y: laserData.points.y,
        drawingData: {
          toolType: "laser",
          id: laserData.id,
          strokeColor: laserData.strokeColor,
          strokeWidth: laserData.strokeWidth,
          isDone,
          pointsSkipped: laserData.pointsSkipped,
        },
      });
    };

    const handleSendRealtimePencilPoint = async (data: any) => {
      if (!data) return;
      await sendPencil({
        x: data.points.x,
        y: data.points.y,
        drawingData: {
          toolType: "pencil",
          id: data.id,
          strokeColor: data.strokeColor,
          strokeWidth: data.strokeWidth,
          isDone: data.isDone,
          lineIdRelated: data.lineIdRelated,
          objectId: data.objectId,
          pointsSkipped: data.pointsSkipped,
        },
      });
    };

    const broadcastCursorPosition = async (e: any) => {
      if (selectedAnnotationMode.value !== AnnotationMode.Cursor || isHelper.value) {
        return;
      }
      const cursorPayload: MousePositionEventData = {
        x: Math.floor(e.pointer?.x ?? prevPoint.value?.x ?? 0),
        y: Math.floor(e.pointer?.y ?? prevPoint.value?.y ?? 0),
      };
      await sendCursor(cursorPayload);
    };

    const handleRealtimePointerMove = async (e: any, isDone = false) => {
      if (selectedAnnotationMode.value === AnnotationMode.Cursor) {
        await broadcastCursorPosition(e);
        return;
      }
      if ((toolSelected.value === Tools.Laser || toolSelected.value === Tools.Pen) && isDrawing.value) {
        const pointer = canvas.getPointer(e);
        const _point = {
          x: Math.floor(pointer?.x ?? prevPoint.value?.x ?? 0),
          y: Math.floor(pointer?.y ?? prevPoint.value?.y ?? 0),
        };
        if (!prevPoint.value || moveEventCounter.value === DIFF_BETWEEN_POINT || !moveEventCounter.value || isDone) {
          prevPoint.value = _point;
        }
        if (moveEventCounter.value === DIFF_BETWEEN_POINT || !moveEventCounter.value || isDone) {
          if (isMouseOut.value) {
            lineId.value = generateRealtimeLineId();
            if (toolSelected.value === Tools.Laser) {
              await handleSendRealtimeLaserPoint({
                data: {
                  id: lineId.value,
                  points: _point,
                  strokeColor: strokeColor.value,
                  strokeWidth: strokeWidth.value,
                  pointsSkipped: pointsSkipped.value,
                },
                isDone,
              });
            } else {
              await handleSendRealtimePencilPoint({
                id: lineId.value,
                points: _point,
                strokeColor: strokeColor.value,
                strokeWidth: strokeWidth.value,
                lineIdRelated: prevLineId.value,
                isDone,
                pointsSkipped: pointsSkipped.value,
                objectId: lineObjectId.value,
              });
            }
            isMouseOver.value = false;
            isMouseOut.value = false;
            prevPoint.value = undefined;
          } else {
            if (toolSelected.value === Tools.Laser) {
              await handleSendRealtimeLaserPoint({
                data: {
                  id: lineId.value,
                  points: _point,
                  strokeColor: strokeColor.value,
                  strokeWidth: strokeWidth.value,
                  pointsSkipped: pointsSkipped.value,
                },
                isDone,
              });
            } else {
              await handleSendRealtimePencilPoint({
                id: lineId.value,
                points: _point,
                strokeColor: strokeColor.value,
                strokeWidth: strokeWidth.value,
                lineIdRelated: "",
                isDone,
                pointsSkipped: pointsSkipped.value,
                objectId: lineObjectId.value,
              });
            }
          }
          return;
        }
      }
    };

    const laserDraw = () => {
      const laserPath = canvas.getObjects(FbObjects.Path).pop();
      laserPath.animate("opacity", "0", {
        duration: 1000,
        easing: fabric.util.ease.easeInOutExpo,
        onChange: canvas.renderAll.bind(canvas),
        onComplete: () => {
          canvas.remove(laserPath);
        },
      });
    };

    /* ----------------------------------------------------------------
     * Canvas event listeners
     * --------------------------------------------------------------*/

    const listenBeforeTransform = () => {
      canvas.on(FbEvents.BeforeTransform, (e: any) => {
        if (isLessonBackgroundObject(e.transform.target)) {
          const sel = new fabric.ActiveSelection(
            canvas.getObjects().filter((item: any) => !isLessonBackgroundObject(item) && !isTargetObject(item)),
            {
              canvas: canvas,
            },
          );
          canvas.setActiveObject(sel);
          canvas.discardActiveObject();
          canvas.renderAll();
        }
      });
    };

    const listenToMouseUp = () => {
      canvas.on(FbEvents.MouseUp, async (event: any) => {
        if (toolSelected.value === Tools.Pen) {
          handleRealtimePointerMove(event.e, true);
          lineId.value = generateRealtimeLineId();
          isDrawing.value = false;
          prevPoint.value = undefined;
          prevLineId.value = "";
          canvas.renderAll();
        }
        if (toolSelected.value === Tools.Laser) {
          handleRealtimePointerMove(event.e, true);
          isDrawing.value = false;
          prevPoint.value = undefined;
          canvas.renderAll();
          laserDraw();
        }
        if (toolSelected.value === Tools.Line && isDrawing.value && lineInProgress.value) {
          isDrawing.value = false;
          // Sync Line to backend after drawing is complete
          onLineCompleted(lineInProgress.value, { handleAddObjectFromCanvas });
        }
      });
    };

    const listenToMouseOut = () => {
      canvas.on(FbEvents.MouseOut, async (_: any) => {
        if (isDrawing.value) {
          isMouseOut.value = true;
        }
      });
    };

    const listenToMouseOver = () => {
      canvas.on(FbEvents.MouseOver, async (_: any) => {
        if (isDrawing.value) {
          isMouseOver.value = true;
        }
      });
    };

    const enableAllNonTargetObjectsSelectable = () => {
      getAllObjectsExceptBackgroundAndTargets(canvas).forEach((item: any) => {
        item.selectable = true;
      });
    };

    const preventProcessAnnotation = ref(false);
    const panning = ref(false);

    const listenMouseEvent = () => {
      canvas.on(FbEvents.MouseMove, (event: any) => {
        moveEventCounter.value += 1;
        handlePointsSkipped(event);
        handleRealtimePointerMove(event);
        if (moveEventCounter.value === DIFF_BETWEEN_POINT) {
          moveEventCounter.value = 0;
          pointsSkipped.value = [];
        }
        switch (toolSelected.value) {
          // handle for TextBox
          case Tools.TextBox: {
            if (!event.target) {
              canvas.setCursor(Cursor.Text);
              canvas.renderAll();
            }
            break;
          }
          case Tools.Line: {
            if (isDrawing.value) {
              const pointer = canvas.getPointer(event);
              lineInProgress.value?.set({
                x2: Math.floor(pointer?.x ?? prevPoint.value?.x ?? 0),
                y2: Math.floor(pointer?.y ?? prevPoint.value?.y ?? 0),
              });
              canvas.renderAll();
            }
            break;
          }
          default:
            break;
        }
        if (panning.value && event && event.e) {
          const delta = new fabric.Point(event.e.movementX, event.e.movementY);
          canvas.relativePan(delta);
        }
      });

      // handle mouse:down
      const handleMouseDown = async (event: any) => {
        switch (toolSelected.value) {
          case Tools.TextBox: {
            if (event.target && !isGroup(event.target)) {
              isEditing.value = true;
              preventProcessAnnotation.value = true;
              break;
            }
            if (!isEditing.value) {
              await createTextBox(
                canvas,
                {
                  top: event.absolutePointer.y + 6.5, // Height of i-beam cursor is 13, + 6.5 to make the textbox appear on the bottom of the cursor
                  left: event.absolutePointer.x,
                },
                controlTextState,
              );
            } else {
              isEditing.value = false;
            }
            preventProcessAnnotation.value = true;
            break;
          }
          case Tools.Laser: {
            isDrawing.value = true;
            preventProcessAnnotation.value = true;
            break;
          }
          case Tools.Pen: {
            prevLineId.value = lineId.value;
            isDrawing.value = true;
            preventProcessAnnotation.value = true;
            break;
          }
          case Tools.Line: {
            if (canvas.getActiveObject(event)) return;

            prevLineId.value = lineId.value;
            isDrawing.value = true;
            preventProcessAnnotation.value = true;

            const pointer = canvas.getPointer(event);
            const _point = {
              x: Math.floor(pointer?.x ?? prevPoint.value?.x ?? 0),
              y: Math.floor(pointer?.y ?? prevPoint.value?.y ?? 0),
            };
            lineInProgress.value = createLine(canvas, _point, strokeColor.value, strokeWidth.value, teacher.value?.id, oneToOneStudentId.value);
            canvas.renderAll();
            break;
          }
          default: {
            const activeFabricObject = canvas.getActiveObject();
            if (!activeFabricObject) panning.value = true;

            break;
          }
        }
      };

      const handleMouseUp = async () => {
        if (toolSelected.value === Tools.Line && isDrawing.value) {
          isDrawing.value = false;
          return;
        }
        if (panning.value) {
          panning.value = false;
          const viewport = canvas.viewportTransform;
          const coords = { x: Math.floor(viewport[4]), y: Math.floor(viewport[5]) };
          await requestMoveZoomedSlide(coords);
          commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), coords, { root: true });
        }
        if (preventProcessAnnotation.value) {
          preventProcessAnnotation.value = false;
          return;
        }
      };

      canvas.on(FbEvents.MouseDown, async (event: any) => {
        if (toolSelected.value === Tools.Pen || toolSelected.value === Tools.Laser) {
          lineObjectId.value = createGuid();
        }
        await handleMouseDown(event);
      });
      canvas.on(FbEvents.MouseUp, handleMouseUp);

      // handle mouse wheel
      canvas.on(FbEvents.MouseWheel, async (opt: any) => {
        const delta = opt.e.deltaY;
        let zoom = canvas.getZoom();
        zoom *= 0.999 ** delta;
        if (zoom > MAX_ZOOM_RATIO) zoom = MAX_ZOOM_RATIO;
        if (zoom < MIN_ZOOM_RATIO) zoom = MIN_ZOOM_RATIO;
        canvas.zoomToPoint(point, zoom);
        opt.e.preventDefault();
        opt.e.stopPropagation();
        localZoomRatio.value = zoom;
        zoomPercentage.value = Math.round(localZoomRatio.value * DEFAULT_ZOOM_PERCENT);
        commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), localZoomRatio.value);
        await sendZoomingState(canvas.viewportTransform, zoom);
      });
    };

    watch(toolSelected, (tool, prevTool) => {
      if (prevTool !== tool && tool !== Tools.TextBox) {
        canvas.discardActiveObject();
        canvas.renderAll();
        isEditing.value = false;
      }
    });

    const listenToCanvasEvents = () => {
      if (isHelper.value) return;
      listenBeforeTransform();
      listenToMouseUp();
      listenToMouseOut();
      listenToMouseOver();
      enableAllNonTargetObjectsSelectable();
      onObjectModified(canvas, {
        handleEditObjectFromCanvas,
      });
      onTextBoxEdited(canvas, { handleEditObjectFromCanvas });
      onObjectSelected(canvas);
      listenMouseEvent();
      onObjectCreated(canvas, {
        handleAddObjectFromCanvas,
        isLaserActive: () => toolSelected.value === Tools.Laser,
      });
      onObjectRemoved(canvas);
      onSelectionChanged(canvas, {
        openTextControlPassively: async (target: any) => {
          if (target.type === FbObjects.Textbox) {
            const getLastCharacterStyles = (target: any) => {
              const lastIndex = target.text.length - 1;
              const lastCharStyles = target.getSelectionStyles(lastIndex, lastIndex + 1)[0];
              const fontSize = lastCharStyles.fontSize || target.fontSize;
              const fill = lastCharStyles.fill || target.fill;
              const fontFamily = lastCharStyles.fontFamily || target.fontFamily;
              return { fontSize, fill, fontFamily };
            };
            const { fontSize, fill, fontFamily } = getLastCharacterStyles(target);
            updateNextStyles({ fontSize, fill, fontFamily });
            updateControlTextState({ fontSize, fill, fontFamily }, true);
            toolSelected.value = Tools.TextBox;
          }
        },
      });
    };

    const setupBoard = async () => {
      canvas = new fabric.Canvas(CANVAS_ID);
      canvas.preserveObjectStacking = true;
      canvas.setWidth(DefaultCanvasDimension.width);
      canvas.setHeight(DefaultCanvasDimension.height);
      initCanvas(canvas);
      point = new fabric.Point(DefaultCanvasDimension.width / 2, DefaultCanvasDimension.height / 2);
      canvas.selectionFullyContained = false;
      try {
        await FontLoader.load();
        await updateCanvasForWhiteboardMode();
      } catch (error) {
        //
      }
      listenToCanvasEvents();
    };

    const disableInteractivityForNonTeacherElements = () => {
      canvas.getObjects().forEach((obj: any) => {
        if (obj.id !== teacher.value?.id && !obj.objectId && !isGroup(obj)) {
          obj.selectable = false;
          obj.hasControls = false;
          obj.hasBorders = false;
          obj.hoverCursor = "cursor";
          obj.perPixelTargetFind = true;
        }
      });
    };

    /* ----------------------------------------------------------------
     * Toolbar handlers (Pen / Laser / Text / Shapes / Clear / Delete)
     * --------------------------------------------------------------*/

    const handleToolClick = async (tool: Tools) => {
      if (pointsSkipped.value.length) {
        pointsSkipped.value = [];
      }
      if (tool === Tools.StrokeColor) {
        disableInteractivityForNonTeacherElements();
        return;
      }
      if (tool === Tools.Stroke) {
        disableInteractivityForNonTeacherElements();
        return;
      }
      canvas.selection = false;
      setCanvasDrawingMode(canvas, tool === Tools.Pen);
      if (toolSelected.value !== tool && tool !== Tools.Delete) {
        toolSelected.value = tool;
      } else {
        if (toolSelected.value === Tools.TextBox) return;
      }
      switch (tool) {
        case Tools.TextBox:
          setDrawMode();
          return;
        case Tools.Cursor:
          toolSelected.value = Tools.Cursor;
          setCanvasDrawingMode(canvas, false);
          setCursorMode();
          disableInteractivityForNonTeacherElements();
          return;
        case Tools.Pen:
          toolSelected.value = Tools.Pen;
          setDrawMode();
          canvas.freeDrawingBrush.color = strokeColor.value;
          canvas.freeDrawingBrush.width = strokeWidth.value;
          disableInteractivityForNonTeacherElements();
          return;
        case Tools.Laser:
          toolSelected.value = Tools.Laser;
          setCanvasDrawingMode(canvas, true);
          setDrawMode();
          return;
        case Tools.Delete: {
          const lastObject = findLatestCreatedItem(getAllUndoableObjects(canvas));
          if (lastObject) await handleRemoveObjectFromCanvas(lastObject);
          toolSelected.value = Tools.Pen;
          setCanvasDrawingMode(canvas, true);
          setDrawMode();
          return;
        }
        case Tools.ClearAll: {
          toolSelected.value = Tools.ClearAll;
          clearCanvasExceptBackgroundAndTargets(canvas);
          handleRemoveAllObjectsFromCanvas();
          toolSelected.value = Tools.Pen;
          setCanvasDrawingMode(canvas, true);
          setDrawMode();
          return;
        }
        case Tools.Circle:
          toolSelected.value = Tools.Circle;
          setDrawMode();
          createCircle(canvas, strokeColor, strokeWidth, oneToOneStudentId);
          disableInteractivityForNonTeacherElements();
          return;
        case Tools.Square:
          toolSelected.value = Tools.Square;
          setDrawMode();
          createSquare(canvas, strokeColor, strokeWidth, oneToOneStudentId);
          disableInteractivityForNonTeacherElements();
          return;
        case Tools.Line:
          toolSelected.value = Tools.Line;
          setDrawMode();
          disableInteractivityForNonTeacherElements();
          return;
        default:
          return;
      }
    };

    const updateColorValue = async (fill: string) => {
      await updateSelectedObjectStyles(canvas, { fill }, handleEditObjectFromCanvas);
      strokeColor.value = fill;
      canvas.freeDrawingBrush.color = fill;
    };

    const updateStrokeWidth = (value: number) => {
      strokeWidth.value = value;
      canvas.freeDrawingBrush.width = value;
    };

    const showWhiteboard = async () => {
      pauseMedia();
      await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_WHITEBOARD), {
        isShowWhiteBoard: true,
      });
      clearCanvasExceptBackground();
      // TODO: make sure toggle whiteboard will clear all annotations on backend
      // await requestClearBoard();
      canvas.freeDrawingBrush.color = strokeColor.value;
      canvas.freeDrawingBrush.width = strokeWidth.value;
    };

    const hideWhiteboard = async () => {
      await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_WHITEBOARD), {
        isShowWhiteBoard: false,
      });
      clearCanvasExceptBackground();
      // TODO: make sure toggle whiteboard will clear all annotations on backend
      // await requestClearBoard();
    };

    /* ----------------------------------------------------------------
     * Slide / image init on canvas
     * --------------------------------------------------------------*/

    const imgRef = ref(null);
    const imgCropRef = ref<any>(null);

    const initializeSlideOnCanvas = ({ propImage, canvas, point, imgEl }: { propImage: any; canvas: any; point: any; imgEl: any }) => {
      const isRotatedSlide = isRotatedLPSlide(propImage.image.metaData);
      const imgWidth = isRotatedSlide ? naturalHeightOfCurrentSlide.value : naturalWidthOfCurrentSlide.value;
      const imgHeight = isRotatedSlide ? naturalWidthOfCurrentSlide.value : naturalHeightOfCurrentSlide.value;
      const canvasGroup = canvas.getObjects().find((item: any) => item?.id === ConnectObjectTypes.LessonBackground);
      const imageRatio = Math.max(imgWidth / DefaultCanvasDimension.width, imgHeight / DefaultCanvasDimension.height);
      const renderWidth = imgWidth / imageRatio;
      const renderHeight = imgHeight / imageRatio;
      if (!propImage.image?.url) {
        return;
      }
      if (canvasGroup) {
        canvas.remove(canvasGroup);
      }
      const clipPath = new fabric.Rect({
        width: renderWidth,
        height: renderHeight,
        left: (DefaultCanvasDimension.width - renderWidth) / 2,
        top: 0,
        absolutePositioned: true,
      });
      const angle = propImage.image.metaData
        ? propImage.image.metaData.width > 0 && propImage.image.metaData.height > 0
          ? 0
          : propImage.image.metaData.rotate
        : 0;
      const Image = new fabric.Image(imgEl, {
        id: ConnectObjectTypes.LessonBackground,
        originX: FbObjectOrigins.Center,
        originY: FbObjectOrigins.Center,
        angle,
        selectable: false,
        hasBorders: false,
        hasControls: false,
      });
      Image.scaleToWidth(renderWidth);
      Image.scaleToHeight(renderHeight);
      const left = DefaultCanvasDimension.width / 2;
      const top = renderHeight / 2;
      const Group = new fabric.Group([Image], {
        id: ConnectObjectTypes.LessonBackground,
        clipPath,
        originX: FbObjectOrigins.Center,
        originY: FbObjectOrigins.Center,
        left,
        top,
        selectable: false,
        hoverCursor: Cursor.Pointer,
        scaleX: propImage.image?.metaData?.scaleX ?? 1,
        scaleY: propImage.image?.metaData?.scaleY ?? 1,
        hasBorders: false,
        hasControls: false,
        layout: "clip-path",
        interactive: true,
        subTargetCheck: true,
        visible: !isWbVisible.value,
      });

      if (top !== Group.top) {
        Group.realTop = Group.top;
      }
      if (left !== Group.left) {
        Group.realLeft = Group.left;
      }
      canvas.add(Group);
      canvas.sendToBack(Group);
      if (localZoomRatio.value > MIN_ZOOM_RATIO) {
        canvas.zoomToPoint(point, localZoomRatio.value);
      }
      if (imgCoords.value) {
        const viewport = canvas.viewportTransform;
        if (imgCoords.value.x) {
          viewport[4] = imgCoords.value.x;
        }
        if (imgCoords.value.y) {
          viewport[5] = imgCoords.value.y;
        }
        canvas.setViewportTransform(viewport);
      }

      return Group;
    };

    const onLPSlideLoaded = async (e?: Event) => {
      if (!canvas || !currentExposureItemMedia.value) return;
      let img: null | HTMLImageElement;
      if (e) {
        img = e?.target as HTMLImageElement;
      } else {
        if (isCropImage.value) {
          img = imgCropRef.value?.imageRef;
        } else {
          img = imgRef.value;
        }
      }
      if (!img) return;
      commit(vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_IMAGE_DIMENSIONS), {
        width: img.naturalWidth && img.naturalHeight ? img.naturalWidth : undefined,
        height: img.naturalWidth && img.naturalHeight ? img.naturalHeight : undefined,
      });
      img.crossOrigin = "anonymous";
      group = await initializeSlideOnCanvas({
        propImage: currentExposureItemMedia.value,
        canvas,
        point,
        imgEl: img,
      });
      if (currentExposureItemMedia.value.image.metaData?.annotations?.length && img.naturalWidth) {
        initializeTargets(currentExposureItemMedia.value.image.metaData);
        addSnapshotCanvasWithTargetsToPreview();
      }
      if (!firstLoadImage.value) {
        firstLoadImage.value = true;
      }
    };

    const resetBoard = async () => {
      setCanvasDrawingMode(canvas, true);
      await setDrawMode();
      // TODO: make sure reset board will clear all annotations on backend
      // await requestClearBoard();
    };

    const onErrorLoadSlide = () => {
      canvas.getObjects().forEach((obj: any) => {
        if (obj.type === FbObjects.Group) {
          canvas.remove(obj);
        }
      });
      handleImageLoadError();
    };

    /* ----------------------------------------------------------------
     * Watchers: image coords, media URL & exposure changes
     * --------------------------------------------------------------*/

    watch(imgCoords, (currentValue) => {
      if (currentValue) {
        const viewport = canvas.viewportTransform;
        viewport[4] = currentValue.x;
        viewport[5] = currentValue.y;
        canvas?.setViewportTransform(viewport);
      }
      canvas?.renderAll();
    });

    watch(
      () => props.image?.url,
      () => {
        video.value?.load();
        audio.value?.load();
        pdfRef.value && scrollPdfToPercentage(0, false);
      },
    );

    watch(isTextBoxSelected, (val) => {
      const cursor = val ? Cursor.Text : Cursor.Pointer;
      canvas.getObjects(FbObjects.Group).forEach((group: any) => {
        group.hoverCursor = cursor;
      });
      canvas.renderAll();
    });

    nextColor.value = strokeColor.value;

    watch(currentExposureItemMedia, async (newValue, oldValue) => {
      clearAllCanvasObjects(canvas);
      clearAllPaths();
      await nextTick();
      if (newValue) {
        localZoomRatio.value = 1;
        zoomPercentage.value = 100;
        canvas.zoomToPoint(point, MIN_ZOOM_RATIO);
        const viewport = canvas.viewportTransform;
        viewport[4] = 0;
        viewport[5] = 0;
        canvas.setViewportTransform(viewport);
        commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), undefined, { root: true });
      } else {
        canvas.remove(group);
      }
      if (newValue && oldValue) {
        if (newValue.id !== oldValue.id) {
          canvas.zoomToPoint(point, MIN_ZOOM_RATIO);
          showHidePreviewModal(false);
          disablePreviewBtn.value = false;
        }
      }
      if (mediaTypeId.value !== undefined && newValue?.image?.url === DEFAULT_LESSON_IMAGE_URL) {
        await dispatch(vuexName(MODULES.LESSON.DISPATCHES.GET_ALTERNATE_MEDIA_URL), {
          id: newValue.id,
          mediaBlobUrl: newValue.image.mediaBlobUrl,
          mediaTypeId: mediaTypeId.value as MediaType,
        });
      }
      if (newValue && oldValue && newValue.image.url === oldValue.image.url) {
        await onLPSlideLoaded();
      }
      resetAudioBackgroundState();
      resetReloadKey();
    });

    watch(oneToOneStudentId, async (newVal, oldVal) => {
      if (!canvas) return;
      const activeFabricObject = canvas.getActiveObject();
      if (activeFabricObject?.type === FbObjects.Textbox && activeFabricObject.isEditing) {
        activeFabricObject.exitEditing();
        canvas.discardActiveObject();
        canvas.renderAll();
      }
      if (!oneToOneStudentId.value) {
        if (oldVal && !newVal) {
          clearAllPaths();
        }
        // await updateCanvasForWhiteboardMode(false);
        scrollPdfToPercentage(prevPdfScrollProgress.value);
        enableAllNonTargetObjectsSelectable();
      } else {
        prevZoomRatio.value = canvas.getZoom();
        prevCoords.value = {
          x: group?.left,
          y: group?.top,
        };
        prevPdfScrollProgress.value = pdfScrollProgress.value;
      }
    });

    /* ----------------------------------------------------------------
     * Video / audio media state & synchronization
     * --------------------------------------------------------------*/

    watch(
      video,
      async (currentVideo) => {
        videoRef.value = currentVideo;
        if (!video.value) return;
        video.value.onloadstart = setSinkId;
        if (!isHelper.value) {
          video.value.onplay = async () => {
            video.value?.setAttribute("dirty", "true");
            const payload = {
              isPlay: true,
              duration: video.value?.currentTime ? parseInt(video.value?.currentTime.toFixed(0)) : 0,
            };
            await debounceSendApiUpdateMediaState(payload);
          };
          video.value.onpause = async () => {
            video.value?.setAttribute("dirty", "true");
            const payload = {
              isPlay: false,
              duration: video.value?.currentTime ? parseInt(video.value?.currentTime.toFixed(0)) : 0,
            };
            await debounceSendApiUpdateMediaState(payload);
          };
          video.value.onseeked = async () => {
            video.value?.setAttribute("dirty", "true");
            const payload = {
              isPlay: !video.value?.paused,
              duration: video.value?.currentTime ? parseInt(video.value?.currentTime.toFixed(0)) : 0,
            };
            await debounceSendApiUpdateMediaState(payload);
          };
        }
      },
      { deep: true },
    );

    watch(
      audio,
      async (currentAudio) => {
        audioRef.value = currentAudio;
        if (!audio.value) return;
        audio.value.onloadstart = setSinkId;
        if (!isHelper.value) {
          audio.value.onplay = async () => {
            const payload = {
              isPlay: true,
              duration: audio.value?.currentTime ? parseInt(audio.value?.currentTime.toFixed(0)) : 0,
            };
            await debounceSendApiUpdateMediaState(payload);
          };
          audio.value.onpause = async () => {
            const payload = {
              isPlay: false,
              duration: audio.value?.currentTime ? parseInt(audio.value?.currentTime.toFixed(0)) : 0,
            };
            await debounceSendApiUpdateMediaState(payload);
          };
          audio.value.onseeked = async () => {
            const payload = {
              isPlay: !audio.value?.paused,
              duration: audio.value?.currentTime ? parseInt(audio.value?.currentTime.toFixed(0)) : 0,
            };
            await debounceSendApiUpdateMediaState(payload);
          };
        }
      },
      { deep: true },
    );

    const alternateMediaState = computed(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_MEDIA_STATE)]);

    watch(
      alternateMediaState,
      () => {
        if (!isHelper.value) {
          return;
        }
        if (video.value) {
          alternateMediaState.value === true ? video.value.play() : video.value.pause();
        }
        if (audio.value) {
          alternateMediaState.value === true ? audio.value.play() : audio.value.pause();
        }
      },
      { deep: true },
    );

    /* ----------------------------------------------------------------
     * PDF rendering & scroll sync (teacher <-> helper)
     * --------------------------------------------------------------*/

    const pdfRef = ref<HTMLElement | null>(null);
    const pdfHeight = ref<number[]>([]);

    const onPdfRendered = () => {
      pdfHeight.value = [];
      document.querySelectorAll(".pdf-config canvas").forEach((element) => {
        pdfHeight.value.push(element.clientHeight);
      });
      if (pdfScrollProgress.value) {
        scrollPdfToPercentage(pdfScrollProgress.value, false);
      }
    };

    const { y: scrollValue, isScrolling } = useScroll(pdfRef);

    watch(isScrolling, async (currentValue, oldValue) => {
      if (isHelper.value) {
        return;
      }

      let scrollPercentage = 0;
      let scrollHeight = scrollValue.value;
      for (const hPage of pdfHeight.value) {
        if (scrollHeight >= hPage) {
          scrollPercentage += 100;
          scrollHeight -= hPage;
        } else if (scrollHeight > 0 && scrollHeight < hPage) {
          scrollPercentage += Number(((scrollHeight / hPage) * 100).toFixed(2));
          scrollHeight = 0;
        }
      }

      if (!currentValue && oldValue) {
        commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), scrollPercentage);
        await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.UPDATE_REMOTE_PDF_SCROLL_PROGRESS), scrollPercentage);
      }
    });

    const pdfScrollProgress = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_PDF_SCROLL_PROGRESS)]);

    const scrollPdfToPercentage = (percentage: number, isSmooth = true) => {
      const pdfElement = document.querySelector(".pdf-config");
      let currentPage = 0;
      let scrollHeight = 0;
      while (percentage > 0) {
        if (percentage >= 100) {
          scrollHeight += pdfHeight.value[currentPage];
          percentage -= 100;
          currentPage++;
        } else {
          scrollHeight += (pdfHeight.value[currentPage] * percentage) / 100;
          percentage = 0;
          currentPage++;
        }
      }
      pdfElement?.scrollTo({
        top: scrollHeight,
        behavior: isSmooth ? "smooth" : "auto",
      });
    };

    watch(pdfScrollProgress, (currentValue) => {
      if (!isHelper.value) {
        return;
      }
      scrollPdfToPercentage(currentValue);
    });

    /* ----------------------------------------------------------------
     * Background audio & audio-local-time
     * --------------------------------------------------------------*/

    const audioLocalTime = ref(0); // audio's actual current time, not from state, in seconds
    const onAudioTimeUpdate = () => {
      audioLocalTime.value = audio.value?.currentTime ?? 0;
    };
    const resetAudioBackgroundState = () => {
      audioLocalTime.value = 0;
    };

    const audioBackground = computed(() => {
      const backgrounds = props.image?.audioBackground;
      if (mediaTypeId.value !== MediaType.mp3 || !backgrounds || !backgrounds.length) {
        return undefined;
      }
      return [...backgrounds];
    });

    const currentAudioBackgroundUrl = computed(() => {
      if (!audioBackground.value) {
        return undefined;
      }
      const currentBackground =
        audioBackground.value?.find((media) => media?.pageTimingToMillisecond && media.pageTimingToMillisecond > audioLocalTime.value * 1000) ??
        audioBackground.value?.[audioBackground.value?.length - 1];
      return currentBackground.image?.url;
    });

    /* ----------------------------------------------------------------
     * TextBox control behavior
     * --------------------------------------------------------------*/

    const closeTextBoxControl = async () => {
      toolSelected.value = Tools.Laser;
      setCanvasDrawingMode(canvas, true);
      await setDrawMode();
    };

    watch(controlTextState, async (val) => {
      if (isControlTextStateWatcherActive) {
        await updateSelectedObjectStyles(canvas, val, handleEditObjectFromCanvas);
      }
    });

    watch(
      () => props.image?.url,
      async () => {
        // Run before old videoRef/audioRef is removed
        if (video.value) {
          video.value.oncanplay = null;
        }
        if (audio.value) {
          audio.value.oncanplay = null;
        }
        toggleMediaCanPlay(false);
        await nextTick();
        // Run after new videoRef/audioRef is created
        if (video.value) {
          videoRef.value = video.value;
          video.value.oncanplay = () => {
            toggleMediaCanPlay(true);
          };
        }
        if (audio.value) {
          audioRef.value = audio.value;
          audio.value.oncanplay = () => {
            toggleMediaCanPlay(true);
          };
        }
      },
    );

    const activeElement = useActiveElement();
    watch(activeElement, () => {
      const activeFabricObject = canvas.getActiveObject();
      if (activeFabricObject?.type === FbObjects.Textbox && activeFabricObject.isEditing) {
        activeFabricObject.hiddenTextarea.focus();
        activeFabricObject.enterEditing();
      }
    });

    /* ----------------------------------------------------------------
     * Audio/video output device (sinkId)
     * --------------------------------------------------------------*/

    const speakerDeviceId = computed(() => getters[vuexName(MODULES.APP.GETTERS.GET_SPEAKER_DEVICE_ID, false)]);

    const setSinkId = async () => {
      if (mediaTypeId.value === MediaType.mp4) {
        const videoEl = document.getElementById(ALTERNATE_VIDEO_ID) as HTMLVideoElement;
        if (typeof videoEl?.setSinkId === "function") {
          await videoEl?.setSinkId(speakerDeviceId.value);
        }
      } else if (mediaTypeId.value === MediaType.mp3) {
        const audioEl = document.getElementById(ALTERNATE_AUDIO_ID) as HTMLAudioElement;
        if (typeof audioEl?.setSinkId === "function") {
          await audioEl?.setSinkId(speakerDeviceId.value);
        }
      }
    };

    watch(speakerDeviceId, setSinkId);

    /* ----------------------------------------------------------------
     * Computed flags: lesson type / prevent draw / localized messages
     * --------------------------------------------------------------*/

    const isCurrentLessonPdf = computed(() => mediaTypeId.value === MediaType.pdf);
    const isCurrentLessonVideo = computed(() => mediaTypeId.value === MediaType.mp4);
    const isPreventDrawOnCanvas = computed(() => (isCurrentLessonPdf.value || isCurrentLessonVideo.value) && !isWbVisible.value);

    const ShowWhiteboardText = computed(() => fmtMsg(TeacherClassLocale.ShowWhiteboard));
    const HideWhiteboardText = computed(() => fmtMsg(TeacherClassLocale.HideWhiteboard));
    const PreviewTargetText = computed(() => fmtMsg(TeacherClassLocale.PreviewTarget));
    const WarningAudioTagText = computed(() => fmtMsg(TeacherClassLocale.WarningAudioTag));
    const WarningVideoTagText = computed(() => fmtMsg(TeacherClassLocale.WarningVideoTag));
    const forTeacherUseOnlyText = computed(() => fmtMsg(TeacherClass.ForTeacherUseOnly));

    /* ----------------------------------------------------------------
     * Lifecycle
     * --------------------------------------------------------------*/

    onMounted(async () => {
      const callbacks = {
        handlePressRemoveIcon,
      };
      initConf(callbacks);
      await setupBoard();
      await resetBoard();
      canvas.freeDrawingBrush.color = Colors.Black;
      canvas.freeDrawingBrush.width = strokeWidth.value;
    });

    onUnmounted(() => {
      canvas.dispose();
      void disconnectLiveCursorSpace();
    });

    /* ----------------------------------------------------------------
     * Expose to template
     * --------------------------------------------------------------*/

    return {
      // messages
      MsgShowAllTargetsBtn: computed(() => fmtMsg(TeacherClass.ShowAllTargets)),
      MsgHideAllTargetsBtn: computed(() => fmtMsg(TeacherClass.HideAllTargets)),
      MsgUnsupportedVideo: computed(() => fmtMsg(ToolboxCanvas.UnsupportedVideo)),
      MsgUnsupportedPdf: computed(() => fmtMsg(ToolboxCanvas.UnsupportedPdf)),

      // flags
      isPreventDrawOnCanvas,
      isCurrentLessonPdf,
      isWbVisible,
      isWhiteboardShrink,
      isTextBoxSelected,
      isTeacherUseOnly,
      isHelper,
      canSendRealtimeAction,
      isCropImage,
      isPointerMode,
      isAllTargetsOnCanvasVisible,
      isAllTargetsOnCanvasTransparent,

      // lesson / media
      currentExposureItemMedia,
      mediaTypeId,
      MediaType,

      // tools
      handleToolClick,
      toolNames,
      toolSelected,
      strokeWidth,
      strokeColor,
      updateColorValue,
      updateStrokeWidth,

      // whiteboard
      showWhiteboard,
      hideWhiteboard,
      ShowWhiteboardText,
      HideWhiteboardText,
      PreviewTargetText,
      WarningAudioTagText,
      WarningVideoTagText,
      forTeacherUseOnlyText,
      CANVAS_ID,

      // slide / targets / preview
      onLPSlideLoaded,
      hasTargets,
      targetsNum,
      targetText,
      handleClickToggleAllTargetsBtn,
      showHidePreviewModal,
      disablePreviewBtn,
      zoomIn,
      zoomOut,
      zoomPercentage,

      // focus word
      focusWordContent,

      // media elements
      video,
      audio,
      isValidUrl,
      ALTERNATE_VIDEO_ID,
      ALTERNATE_AUDIO_ID,
      volume,

      // image refs
      imgRef,
      imgCropRef,
      whiteBoardRef,

      // pointer
      pointerStyle,

      // pdf
      pdfRef,
      onPdfRendered,

      // audio background
      currentAudioBackgroundUrl,
      onAudioTimeUpdate,

      // canvas helpers
      deactiveObject,
      clearCanvasExceptBackground,
      handleMouseOver,
      handleMouseOut,

      // text control
      closeTextBoxControl,
      controlTextState,
      updateControlTextState,

      // reload / error
      reloadKey,
      onErrorLoadSlide,
    };
  },
});
